import { GenericOAuth2 } from '@capacitor-community/generic-oauth2';
// Plugin-registreringen skjer i src/capacitor-plugin-registration.ts (importert
// først i src/index.ts) som setter en statisk web-factory FØR pakkens egen
// dynamiske `() => import('./web')` får sjansen til å feile via Vite sin
// preload-error-håndtering. Se issue #12862 / #12824.
import { jwtDecode } from 'jwt-decode';
import { t } from 'src/i18n';
import { displayAlert } from 'src/utilities/display-alert';
import { runningUnderNativePlatform } from 'src/store/running-under-native-platform';
import { networkState } from 'src/utilities/network-error-handler';
import { buildCriiptoLoginAdditionalParameters } from 'src/auth/criipto-login-prompt';
import {
  clearOAuthProcessing,
  clearTokens as clearStoredTokens,
  getAccessToken as getStoredAccessToken,
  getRefreshToken as getStoredRefreshToken,
  hasAccessToken as hasStoredAccessToken,
  markOAuthProcessing,
  persistAuthenticatedSession,
  saveTokens as saveStoredTokens,
} from 'src/auth/auth-storage.js';
import { criiptoLogin } from './criiptoLogin';
import { helseIDLogin } from './helseIDLogin';
import { refreshToken } from './refreshToken';

// Custom error class to distinguish refresh token expiry from other errors
export class RefreshTokenExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RefreshTokenExpiredError';
  }
}

const isNative = runningUnderNativePlatform();

export async function saveTokens(accessToken: string, refreshToken: string) {
  if (!isNative) {
    console.log('Using localStorage for tokens (web environment)');
  }
  await saveStoredTokens(accessToken, refreshToken);
}

export async function hasAccessToken(): Promise<boolean> {
  return hasStoredAccessToken();
}

// Get tokens
export async function getAccessToken() {
  return getStoredAccessToken();
}

export async function getRefreshToken() {
  return getStoredRefreshToken();
}

// Remove tokens (on logout)
export async function clearTokens() {
  await clearStoredTokens();

  // Clear offline attachment cache on logout (native only)
  const { attachmentCache } = await import('src/services/attachment-cache');
  await attachmentCache.clearAll();
}

const criiptoOptions = {
  authorizationBaseUrl: 'https://trinnvis.criipto.id/oauth2/authorize',
  accessTokenEndpoint: 'https://trinnvis.criipto.id/oauth2/token',
  scope: 'profile openid offline_access',
  logsEnabled: true,
  additionalParameters: buildCriiptoLoginAdditionalParameters(false, {
    acr_values: 'urn:grn:authn:no:bankid',
  }),
  appId: 'urn:dabih-app:identifier:695713',
  web: {
    responseType: 'code',
    redirectUrl: window.origin,
    windowTarget: '_self',
    pkceEnabled: true,
    sendCacheControlHeader: false,
    // Additional headers that might help with Safari
    additionalResourceHeaders: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  },
  android: {
    responseType: 'code',
    redirectUrl: 'no.trinnvis.dabih.issues://callback',
    pkceEnabled: true,
  },
  ios: {
    responseType: 'code',
    redirectUrl: 'no.trinnvis.dabih://callback',
    pkceEnabled: true,
  },
};

function getCriiptoOptions(forcePromptLogin: boolean) {
  return {
    ...criiptoOptions,
    additionalParameters: buildCriiptoLoginAdditionalParameters(forcePromptLogin, {
      acr_values: 'urn:grn:authn:no:bankid',
    }),
  };
}

export async function openCriiptoAuth(forcePromptLogin: boolean) {
  try {
    const currentCriiptoOptions = getCriiptoOptions(forcePromptLogin);

    console.log(
      'BankID auth initiated - Platform:',
      isNative ? 'Native' : 'Web',
      'Window target:',
      currentCriiptoOptions.web.windowTarget,
    );
    console.log('Criipto options:', JSON.stringify(currentCriiptoOptions, null, 2));

    const r = await GenericOAuth2.authenticate(currentCriiptoOptions);
    console.log('OAuth2 response received:', {
      hasAccessTokenResponse: !!r.access_token_response,
      hasIdToken: !!r.access_token_response?.id_token,
      accessTokenKeys: r.access_token_response ? Object.keys(r.access_token_response) : [],
      fullResponse: JSON.stringify(r, null, 2),
    });

    const currentAccessToken = await getValidAccessToken();
    console.log('Current access token retrieved:', currentAccessToken ? 'exists' : 'empty');

    // Validate OAuth response structure before accessing properties
    if (!r.access_token_response?.id_token) {
      const errorMsg = 'BankID OAuth response missing id_token';
      console.error(errorMsg, {
        hasAccessTokenResponse: !!r.access_token_response,
        responseStructure: r.access_token_response ? Object.keys(r.access_token_response) : 'null/undefined',
      });
      throw new Error(errorMsg);
    }

    const criiptoRequest = {
      criiptoTokenLoginMessage: {
        idToken: r.access_token_response.id_token,
        currentAccessToken: currentAccessToken != '' ? currentAccessToken : undefined,
      },
    };
    console.log('Criipto login request:', JSON.stringify(criiptoRequest, null, 2));

    const l = await criiptoLogin(criiptoRequest);
    console.log('Criipto login response:', JSON.stringify(l, null, 2));

    // Validate API response before accessing properties
    if (!l?.username || !l?.accessToken || !l?.refreshToken) {
      const errorMsg = 'Criipto API response missing required fields';
      console.error(errorMsg, {
        hasUsername: !!l?.username,
        hasAccessToken: !!l?.accessToken,
        hasRefreshToken: !!l?.refreshToken,
        responseKeys: l ? Object.keys(l) : 'null/undefined',
      });
      throw new Error(errorMsg);
    }

    await persistAuthenticatedSession({
      username: l.username,
      provider: 'criipto',
      accessToken: l.accessToken,
      refreshToken: l.refreshToken,
    });
    console.log('Tokens saved successfully');

    window.location.href = '/';
  } catch (error: any) {
    console.error('BankID authentication failed - Full error:', error);
    if (error?.response) {
      console.error('Error response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      });
    }

    // Provide more specific error messages based on error type
    let errorMessage = t('auth.bankIdLoginFailed');

    // Check for Capacitor plugin loading errors (dynamic import failure)
    if (error?.message?.includes('GenericOAuth2Web') || error?.message?.includes('undefined')) {
      console.error('Capacitor plugin loading error - web implementation failed to load');
      errorMessage = t('auth.bankIdLoginFailedPluginUnavailable');
    } else if (error?.message?.includes('missing id_token')) {
      errorMessage = t('auth.bankIdLoginFailedInvalidResponse');
    } else if (error?.message?.includes('missing required fields')) {
      errorMessage = t('auth.bankIdLoginFailedInvalidServerResponse');
    } else if (error?.response?.status === 404) {
      errorMessage = t('auth.bankIdLoginFailedUserNotFound');
    } else if (error?.response?.status >= 500) {
      errorMessage = t('auth.bankIdLoginFailedServerError');
    } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      errorMessage = t('auth.bankIdLoginFailedNetworkError');
    }

    displayAlert(errorMessage);
    throw error;
  }
}

const helseIdOptions = {
  authorizationBaseUrl: 'https://api.trinnvis.io/auth/helseid/authorize',
  accessTokenEndpoint: 'https://api.trinnvis.io/auth/helseid/token',
  scope: 'email profile openid offline_access',
  logsEnabled: true,
  appId: '6ebb3644-762e-41b8-b0eb-a45df5698a6a',
  web: {
    responseType: 'code', // implicit flow
    redirectUrl: window.origin,
    windowTarget: '_self',
    pkceEnabled: true,
    sendCacheControlHeader: false,
  },
  android: {
    responseType: 'code',
    redirectUrl: 'no.trinnvis.dabih.issues://callback',
    pkceEnabled: true,
  },
  ios: {
    responseType: 'code',
    redirectUrl: 'no.trinnvis.dabih://callback',
    pkceEnabled: true,
    logsEnabled: true,
  },
};

function getHelseIdOptions(forcePromptLogin: boolean) {
  if (!forcePromptLogin) {
    return helseIdOptions;
  }
  return {
    ...helseIdOptions,
    additionalParameters: { prompt: 'login' },
  };
}

async function executeHelseIdLogin(r: any, currentAccessToken: string | string) {
  // Try to find id_token in multiple possible locations in the response
  let idToken: string | undefined;

  if (r.access_token_response?.id_token) {
    idToken = r.access_token_response.id_token;
  } else if (r.access_token_response?.idToken) {
    // Try camelCase variant
    idToken = r.access_token_response.idToken;
  } else if ((r as any).id_token) {
    // Try top-level id_token
    idToken = (r as any).id_token;
  } else if ((r as any).idToken) {
    // Try top-level idToken (camelCase)
    idToken = (r as any).idToken;
  }

  // Validate that we found an id_token
  if (!idToken) {
    console.error('HelseID OAuth response missing id_token in all expected locations:', {
      hasAccessTokenResponse: !!r.access_token_response,
      accessTokenResponseKeys: r.access_token_response ? Object.keys(r.access_token_response) : [],
      topLevelKeys: Object.keys(r),
      fullResponse: JSON.stringify(r, null, 2),
    });
    throw new Error('HelseID OAuth response did not include id_token. Please try again or contact support.');
  }

  console.log('Found id_token, length:', idToken.length, 'first 50 chars:', idToken.substring(0, 50) + '...');

  const l = await helseIDLogin({
    helseIDTokenLoginMessage: {
      idToken: idToken,
      currentAccessToken: currentAccessToken != '' ? currentAccessToken : undefined,
    },
  });
  console.log('SecureStoragePlugin authenticated l', l);

  // Validate API response before accessing properties
  if (!l?.username || !l?.accessToken || !l?.refreshToken) {
    const errorMsg = 'HelseID API response missing required fields';
    console.error(errorMsg, {
      hasUsername: !!l?.username,
      hasAccessToken: !!l?.accessToken,
      hasRefreshToken: !!l?.refreshToken,
      responseKeys: l ? Object.keys(l) : 'null/undefined',
    });
    throw new Error(errorMsg);
  }

  await persistAuthenticatedSession({
    username: l.username,
    provider: 'oidc',
    accessToken: l.accessToken,
    refreshToken: l.refreshToken,
  });

  console.log('SecureStoragePlugin saveTokens');

  window.location.href = '/';
}

export async function openHelseIdAuth(forcePromptLogin: boolean) {
  try {
    const currentHelseIdOptions = getHelseIdOptions(forcePromptLogin);
    console.log(
      'HelseID auth initiated - Platform:',
      isNative ? 'Native' : 'Web',
      'Window target:',
      currentHelseIdOptions.web.windowTarget,
    );

    const r = await GenericOAuth2.authenticate(currentHelseIdOptions);
    console.log('OAuth2 authenticated successfully. Full response:', {
      hasAccessTokenResponse: !!r.access_token_response,
      hasIdToken: !!r.access_token_response?.id_token,
      accessTokenResponseKeys: r.access_token_response ? Object.keys(r.access_token_response) : [],
      fullResponse: JSON.stringify(r, null, 2),
    });

    const currentAccessToken = await getValidAccessToken();
    console.log('Current access token retrieved:', currentAccessToken ? 'exists' : 'empty');

    await executeHelseIdLogin(r, currentAccessToken);
  } catch (error: any) {
    console.error('HelseID authentication failed:', error);

    // Provide more specific error messages based on error type
    let errorMessage = t('auth.helseIdLoginFailed');

    // Check for Capacitor plugin loading errors (dynamic import failure)
    if (error?.message?.includes('GenericOAuth2Web') || error?.message?.includes('undefined')) {
      console.error('Capacitor plugin loading error - web implementation failed to load');
      errorMessage = t('auth.helseIdLoginFailedPluginUnavailable');
    } else if (error?.message?.includes('missing id_token')) {
      errorMessage = t('auth.helseIdLoginFailedInvalidResponse');
    } else if (error?.message?.includes('missing required fields')) {
      errorMessage = t('auth.helseIdLoginFailedInvalidServerResponse');
    } else if (error?.response?.status === 404) {
      errorMessage = t('auth.helseIdLoginFailedUserNotFound');
    } else if (error?.response?.status >= 500) {
      errorMessage = t('auth.helseIdLoginFailedServerError');
    } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      errorMessage = t('auth.helseIdLoginFailedNetworkError');
    }

    displayAlert(errorMessage);
    throw error;
  }
}

/**
 * Check if refresh token has expired.
 * Checks both rolling expiration (exp) and absolute maximum lifetime (first_issued_at).
 *
 * Refresh token contains:
 * - exp: Rolling expiration (8 hours of inactivity for WEB_BROWSER)
 * - first_issued_at: When the first token in the chain was issued (for absolute 5-year limit)
 * - iat: When this specific token was issued
 * - client_type: WEB_BROWSER, MOBILE_APP, etc.
 * - sub: User identifier
 * - provider: Auth provider
 *
 * SECURITY NOTE: We decode the JWT without signature validation, which is safe here because:
 * 1. We only read the expiration claims (exp, first_issued_at) - no authorization decisions
 * 2. The actual token is still validated by the backend API when used
 * 3. If someone manipulates the token, they'll get a 412 error from the API and be logged out
 * 4. This prevents unnecessary API calls for obviously expired tokens, reducing server load
 *
 * @param refreshToken - The JWT refresh token to check
 * @returns true if token is expired, false otherwise
 */
export function isRefreshTokenExpired(refreshToken: string): boolean {
  try {
    // Decode JWT payload (part 2 of token)
    // nosemgrep: jwt-decode-without-verify
    const payload = JSON.parse(atob(refreshToken.split('.')[1]));

    const now = Date.now();

    // Check rolling expiration (most important check!)
    // This expires first (8 hours for WEB_BROWSER)
    if (payload.exp * 1000 < now) {
      console.log('[isRefreshTokenExpired] Refresh token expired (rolling window)', {
        exp: payload.exp,
        now: Math.floor(now / 1000),
        expired: true,
      });
      return true;
    }

    // Check absolute maximum lifetime (5 years - rarely a problem)
    const firstIssuedAt = payload.first_issued_at;
    const maxLifetimeMs = 5 * 365 * 24 * 60 * 60 * 1000; // 5 years
    if (firstIssuedAt && now - firstIssuedAt > maxLifetimeMs) {
      console.log('[isRefreshTokenExpired] Refresh token exceeded absolute maximum lifetime', {
        firstIssuedAt,
        now,
        age: now - firstIssuedAt,
        maxLifetime: maxLifetimeMs,
      });
      return true;
    }

    console.log('[isRefreshTokenExpired] Refresh token is valid', {
      exp: payload.exp,
      timeUntilExpiry: payload.exp - Math.floor(now / 1000),
    });
    return false;
  } catch (_e) {
    console.error('[isRefreshTokenExpired] Failed to decode refresh token:', _e);
    return true;
  }
}

// Decode and check token expiration
function isTokenExpired(token: string): boolean {
  try {
    const decodedToken: any = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const expired = decodedToken.exp < currentTime;
    console.log('[isTokenExpired] Token expiry check:', {
      exp: decodedToken.exp,
      currentTime,
      expired,
      timeUntilExpiry: decodedToken.exp - currentTime,
    });
    return expired;
  } catch (error) {
    console.error('[isTokenExpired] Failed to decode token:', error);
    return true; // Consider token expired if decoding fails
  }
}

// Refresh access token
async function refreshAccessToken() {
  console.log('[refreshAccessToken] Starting token refresh...');
  const token = await getRefreshToken();
  console.log('[refreshAccessToken] Refresh token:', token ? `${token.substring(0, 20)}...` : 'none');

  if (!token) {
    console.error('[refreshAccessToken] No refresh token available');
    throw new Error('No refresh token available');
  }

  // Check if refresh token is expired BEFORE calling the API
  // This prevents unnecessary 412 errors from being sent to monitoring
  if (isRefreshTokenExpired(token)) {
    console.error('[refreshAccessToken] Refresh token is expired - cannot refresh access token');
    throw new RefreshTokenExpiredError('Refresh token is expired');
  }

  // OPTIMIZATION: Skip API call if we know we're offline
  // This prevents unnecessary error logs and improves performance
  if (!networkState.isOnline) {
    console.log('[refreshAccessToken] Offline - skipping API call, using cached access token');
    const existingAccessToken = await getAccessToken();
    if (existingAccessToken) {
      return existingAccessToken;
    }
    // No cached access token available - throw error
    // Heartbeat will check refresh token validity and decide whether to logout
    console.error('[refreshAccessToken] Offline and no cached access token available');
    throw new Error('Cannot refresh access token while offline without cached token');
  }

  try {
    console.log('[refreshAccessToken] Calling refresh token API...');
    const response = await refreshToken({
      refreshTokenMessage: {
        refreshToken: token,
      },
    });

    const newAccessToken = response.accessToken;
    const newRefreshToken = response.refreshToken;
    console.log('[refreshAccessToken] Received new tokens:', {
      accessToken: newAccessToken ? `${newAccessToken.substring(0, 20)}...` : 'none',
      refreshToken: newRefreshToken ? `${newRefreshToken.substring(0, 20)}...` : 'none',
    });

    // Hindrer "stille suksess" der API-en svarer 200 men access/refresh-token er
    // tom — det førte til evig 401-loop i createScreen/lignende kall (#12911).
    if (!newAccessToken || !newRefreshToken) {
      console.error('[refreshAccessToken] Refresh API returned empty token(s)', {
        hasAccessToken: !!newAccessToken,
        hasRefreshToken: !!newRefreshToken,
        responseKeys: response ? Object.keys(response) : 'null/undefined',
      });
      throw new RefreshTokenExpiredError('Refresh API returned empty tokens');
    }

    // Save new tokens
    console.log('[refreshAccessToken] Saving new tokens...');
    await saveTokens(newAccessToken, newRefreshToken);
    console.log('[refreshAccessToken] Token refresh completed successfully');
    return newAccessToken;
  } catch (error) {
    // If refresh token is expired, don't use cached access token fallback
    // Let the error propagate so heartbeat can handle logout properly
    if (error instanceof RefreshTokenExpiredError) {
      console.error('[refreshAccessToken] Refresh token expired - not using cached token fallback');
      throw error;
    }

    // ROBUST MODE: If token refresh fails (offline, timeout, server error, etc.)
    // but refresh token is still valid, return cached access token as fallback
    // This allows user to continue using cached data
    console.error('[refreshAccessToken] Failed to refresh access token:', error);

    // Log detailed error information to help diagnose 412 errors
    const errorStatus = (error as any)?.status || (error as any)?.response?.status;
    console.error('[refreshAccessToken] Error details:', {
      status: errorStatus,
      message: (error as Error)?.message,
      name: (error as Error)?.name,
    });

    // If we get a 412, check if refresh token is actually expired
    if (errorStatus === 412) {
      const currentToken = await getRefreshToken();
      const tokenExpired = currentToken ? isRefreshTokenExpired(currentToken) : true;
      console.error('[refreshAccessToken] Got 412 from server, checking token expiry:', {
        tokenExists: !!currentToken,
        isExpired: tokenExpired,
        tokenPreview: currentToken ? currentToken.substring(0, 20) + '...' : 'none',
      });

      // If token is expired according to our check, don't use cached fallback
      if (tokenExpired) {
        console.error('[refreshAccessToken] Server returned 412 and token is expired - not using cached token');
        throw new RefreshTokenExpiredError('Refresh token expired (confirmed by 412 response)');
      }
    }

    console.log('[refreshAccessToken] Attempting to use cached access token as fallback');

    const existingAccessToken = await getAccessToken();
    if (existingAccessToken) {
      console.log('[refreshAccessToken] Using cached access token despite refresh failure');
      console.log('[refreshAccessToken] User can continue with cached data');
      return existingAccessToken;
    }

    // No cached access token available - re-throw error
    // Heartbeat will check refresh token validity and decide whether to logout
    console.error('[refreshAccessToken] No cached access token available');
    throw error;
  }
}

// Get access token (with automatic refresh)
export async function getValidAccessToken() {
  const hasToken = await hasAccessToken();

  // Fix for issue #12431: If no access_token, check if we have a refresh_token
  // and try to use it to get a new access_token
  if (!hasToken) {
    const storedRefreshToken = await getRefreshToken();
    if (storedRefreshToken) {
      try {
        return await refreshAccessToken();
      } catch (error) {
        console.error('[getValidAccessToken] Refresh failed:', error);
        return '';
      }
    }
    return '';
  }

  let accessToken = await getAccessToken();
  const expired = accessToken ? isTokenExpired(accessToken) : true;

  if (!accessToken || expired) {
    accessToken = await refreshAccessToken();
  }

  return accessToken;
}

// Track if we've already processed this code to prevent duplicate calls
let processingCode = false;
const processedCodes = new Set<string>();

// Export processing state so UI can check it
export function isProcessingOAuthCode(): boolean {
  return processingCode;
}

export async function checkRedirectCodeFlow() {
  console.log('[checkRedirectCodeFlow] Called from:', new Error().stack?.split('\n')[2]);

  if (runningUnderNativePlatform()) {
    console.log('[checkRedirectCodeFlow] Running under native platform - skipping');
    return;
  }

  const query = location.search.slice(1);
  const params = new URLSearchParams(query);
  console.log('[checkRedirectCodeFlow] URL params:', location.search);

  // Set a flag in sessionStorage to indicate OAuth processing
  if (params.has('code')) {
    markOAuthProcessing();
  }

  const currentAccessToken = await getValidAccessToken();

  if (params.has('code')) {
    const code = params.get('code')!;
    const timestamp = Date.now();

    console.log(`[checkRedirectCodeFlow] Found code at ${timestamp}:`, code.substring(0, 10) + '...');
    console.log(`[checkRedirectCodeFlow] processingCode flag: ${processingCode}`);
    console.log(`[checkRedirectCodeFlow] Already processed: ${processedCodes.has(code)}`);

    // Check if we're already processing this code or have processed it before
    if (processingCode || processedCodes.has(code)) {
      console.log(`[checkRedirectCodeFlow] SKIPPING duplicate code processing for: ${code.substring(0, 10)}...`);
      return;
    }

    // Mark that we're processing this code
    processingCode = true;
    processedCodes.add(code);
    console.log(`[checkRedirectCodeFlow] Starting to process code at ${timestamp}`);

    // Store the original URL with code before cleaning
    const originalUrl = window.location.href;

    // Clean the URL immediately to prevent duplicate processing
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
    console.log('[checkRedirectCodeFlow] Cleaned URL to prevent duplicate processing');

    const iss = params.get('iss');
    console.log('[checkRedirectCodeFlow] Processing OAuth - iss:', iss, 'original URL:', originalUrl);

    try {
      if (iss === 'https://helseid-sts.nhn.no') {
        try {
          console.log('[checkRedirectCodeFlow] Calling HelseID redirectFlowCodeListener...');
          const r = await GenericOAuth2.redirectFlowCodeListener({
            ...helseIdOptions,
            response_url: originalUrl,
          });
          console.log('[checkRedirectCodeFlow] HelseID redirect flow response:', {
            hasAccessTokenResponse: !!r.access_token_response,
            hasIdToken: !!r.access_token_response?.id_token,
            accessTokenResponseKeys: r.access_token_response ? Object.keys(r.access_token_response) : [],
            fullResponse: JSON.stringify(r, null, 2),
          });
          await executeHelseIdLogin(r, currentAccessToken);
        } catch (error) {
          console.error('[checkRedirectCodeFlow] HelseID authentication error:', error);
          displayAlert(t('auth.helseIdUserNotFound'));
        }
      } else {
        // Criipto does not provide iss
        try {
          console.log('[checkRedirectCodeFlow] Calling Criipto redirectFlowCodeListener...');
          console.log('[checkRedirectCodeFlow] Criipto options:', JSON.stringify(getCriiptoOptions(false), null, 2));
          const r = await GenericOAuth2.redirectFlowCodeListener({
            ...getCriiptoOptions(false),
            response_url: originalUrl,
          });
          console.log('Criipto redirectFlowCodeListener response:', {
            hasAccessTokenResponse: !!r.access_token_response,
            hasIdToken: !!r.access_token_response?.id_token,
            accessTokenKeys: r.access_token_response ? Object.keys(r.access_token_response) : [],
            fullResponse: JSON.stringify(r, null, 2),
          });

          // Validate OAuth response structure before accessing properties
          if (!r.access_token_response?.id_token) {
            const errorMsg = 'BankID OAuth redirect response missing id_token';
            console.error(errorMsg, {
              hasAccessTokenResponse: !!r.access_token_response,
              responseStructure: r.access_token_response ? Object.keys(r.access_token_response) : 'null/undefined',
            });
            throw new Error(errorMsg);
          }

          const criiptoRequest = {
            criiptoTokenLoginMessage: {
              idToken: r.access_token_response.id_token,
              currentAccessToken: currentAccessToken != '' ? currentAccessToken : undefined,
            },
          };
          console.log('Criipto login request (redirect flow):', JSON.stringify(criiptoRequest, null, 2));

          const l = await criiptoLogin(criiptoRequest);
          console.log('Criipto login response (redirect flow):', JSON.stringify(l, null, 2));

          // Validate API response before accessing properties
          if (!l?.username || !l?.accessToken || !l?.refreshToken) {
            const errorMsg = 'Criipto API redirect response missing required fields';
            console.error(errorMsg, {
              hasUsername: !!l?.username,
              hasAccessToken: !!l?.accessToken,
              hasRefreshToken: !!l?.refreshToken,
              responseKeys: l ? Object.keys(l) : 'null/undefined',
            });
            throw new Error(errorMsg);
          }

          await persistAuthenticatedSession({
            username: l.username,
            provider: 'criipto',
            accessToken: l.accessToken,
            refreshToken: l.refreshToken,
          });

          console.log('Tokens saved successfully (redirect flow)');
        } catch (error: any) {
          console.error('Criipto redirect flow failed - Full error:', error);
          if (error?.response) {
            console.error('Error response:', {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
              headers: error.response.headers,
            });
          }
          displayAlert(t('auth.bankIdUserNotFound'));
        }

        window.location.href = '/';
      }
    } finally {
      // Reset the processing flag so future legitimate codes can be processed
      processingCode = false;
      clearOAuthProcessing();
      console.log(`[checkRedirectCodeFlow] Reset processingCode flag and cleared sessionStorage at ${Date.now()}`);
    }
  } else {
    console.log('[checkRedirectCodeFlow] No code parameter found in URL');
    clearOAuthProcessing();
  }
}
